import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../utils/api';

const ORIGINS = ['USA, Miami', 'Canada', 'Panama'];
const SERVICES = ['ocean', 'air'];
const RATE_TABS = ['Ocean / Air', 'Brokerage', 'Transport'];

export default function Rates() {
  const { toast, setRates: setGlobalRates } = useApp();
  const [rateTab, setRateTab] = useState(0);
  const [rates, setRates] = useState([]);
  const [origin, setOrigin] = useState('USA, Miami');
  const [service, setService] = useState('ocean');
  const [brokerageRates, setBrokerageRates] = useState([]);
  const [cifTiers, setCifTiers] = useState([]);
  const [transportZones, setTransportZones] = useState([]);
  const [transportParams, setTransportParams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [newRate, setNewRate] = useState({ name:'', category:'freight', currency:'USD', value:'' });

  useEffect(() => { loadRates(); }, [origin, service]);
  useEffect(() => {
    api.get('/brokerage-rates').then(setBrokerageRates).catch(()=>{});
    api.get('/brokerage-tiers').then(setCifTiers).catch(()=>{});
    api.get('/transport-zones').then(setTransportZones).catch(()=>{});
    api.get('/transport-params').then(setTransportParams).catch(()=>{});
  }, []);

  const loadRates = async () => {
    setLoading(true);
    try { setRates(await api.get(`/rates?origin=${encodeURIComponent(origin)}&service=${service}`)); }
    catch(e) { toast(e.message,'error'); }
    finally { setLoading(false); }
  };

  const flash = () => { setSaved(true); setTimeout(()=>setSaved(false),3000); };

  const saveRates = async () => {
    try {
      await api.put('/rates', { rates: rates.map(r=>({ category:r.category, name:r.name, currency:r.currency, value:r.value, origin:r.origin, service:r.service })) });
      const all = await api.get('/rates');
      setGlobalRates(all);
      flash(); toast('Rates saved','success');
    } catch(e) { toast(e.message,'error'); }
  };

  const saveBrokerage = async () => {
    try {
      await api.put('/brokerage-rates', { rates: brokerageRates });
      await api.put('/brokerage-tiers', { tiers: cifTiers });
      flash(); toast('Brokerage rates saved','success');
    } catch(e) { toast(e.message,'error'); }
  };

  const saveTransport = async () => {
    try {
      await api.put('/transport-zones', { zones: transportZones });
      await api.put('/transport-params', { params: transportParams });
      flash(); toast('Transport rates saved','success');
    } catch(e) { toast(e.message,'error'); }
  };

  const addRate = async () => {
    if (!newRate.name || newRate.value==='') { toast('Name and value required','error'); return; }
    try {
      await api.put('/rates', { rates:[{ ...newRate, value:parseFloat(newRate.value), origin, service }] });
      setAddModal(false);
      setNewRate({ name:'', category:'freight', currency:'USD', value:'' });
      loadRates(); toast('Rate added','success');
    } catch(e) { toast(e.message,'error'); }
  };

  const setRateVal = (id, value) => setRates(r=>r.map(x=>x.id===id?{...x,value:parseFloat(value)||0}:x));
  const setBRVal = (id, field, value) => setBrokerageRates(r=>r.map(x=>x.id===id?{...x,[field]:parseFloat(value)||0}:x));
  const setTierVal = (id, field, value) => setCifTiers(t=>t.map(x=>x.id===id?{...x,[field]:parseFloat(value)||0}:x));
  const setZoneVal = (id, field, value) => setTransportZones(z=>z.map(x=>x.id===id?{...x,[field]:parseFloat(value)||0}:x));
  const setParamVal = (key, value) => setTransportParams(p=>p.map(x=>x.key===key?{...x,value:parseFloat(value)||0}:x));

  const freight = rates.filter(r=>r.category==='freight');
  const local = rates.filter(r=>r.category==='local');

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Rates & pricing</div><div className="page-sub">Manage all freight, brokerage, and transport rates</div></div>
      </div>

      {/* Rate section tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:24, borderBottom:'1px solid var(--border)' }}>
        {RATE_TABS.map((t,i)=>(
          <button key={t} onClick={()=>setRateTab(i)} style={{ padding:'8px 20px', border:'none', background:'none', fontFamily:'inherit', fontSize:14, cursor:'pointer', borderBottom: rateTab===i?'2px solid var(--navy)':'2px solid transparent', color: rateTab===i?'var(--navy)':'var(--light)', fontWeight: rateTab===i?600:400, marginBottom:-1 }}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Ocean / Air tab ──────────────────────────────────────────── */}
      {rateTab===0 && (
        <>
          <div className="card" style={{ marginBottom:20, padding:'16px 22px' }}>
            <div className="gap-row">
              <div>
                <div style={{ fontSize:11, color:'var(--light)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Origin</div>
                <div className="gap-row">{ORIGINS.map(o=><button key={o} className={`btn btn-sm ${origin===o?'btn-primary':'btn-outline'}`} onClick={()=>setOrigin(o)}>{o}</button>)}</div>
              </div>
              <div style={{ width:1, background:'var(--border)', alignSelf:'stretch', margin:'0 4px' }}/>
              <div>
                <div style={{ fontSize:11, color:'var(--light)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Service</div>
                <div className="gap-row">{SERVICES.map(s=><button key={s} className={`btn btn-sm ${service===s?'btn-primary':'btn-outline'}`} onClick={()=>setService(s)}>{s==='ocean'?'Ocean LCL':'Air Freight'}</button>)}</div>
              </div>
              <div style={{ marginLeft:'auto' }} className="gap-row">
                <button className="btn btn-outline" onClick={()=>setAddModal(true)}>+ Add rate</button>
                <button className="btn btn-green" onClick={saveRates}>{saved?'✓ Saved':'Save changes'}</button>
              </div>
            </div>
          </div>
          {loading ? <div style={{ textAlign:'center', padding:60, color:'var(--light)' }}>Loading…</div> : (
            <div className="grid-2">
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--mid)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Freight charges (USD)</div>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Charge</th><th>Currency</th><th>Rate</th><th>Basis</th></tr></thead>
                    <tbody>
                      {freight.length===0 ? <tr><td colSpan={4} style={{ color:'var(--light)', padding:'24px 16px' }}>No freight rates configured</td></tr>
                      : freight.map(r=>(
                        <tr key={r.id}>
                          <td style={{ fontWeight:500 }}>{r.name}</td>
                          <td><span className={`badge ${r.currency==='USD'?'badge-blue':'badge-green'}`}>{r.currency}</span></td>
                          <td><input className="input" type="number" step="0.0001" value={r.value} onChange={e=>setRateVal(r.id,e.target.value)} style={{ width:110, padding:'5px 8px' }} /></td>
                          <td style={{ fontSize:11, color:'var(--light)' }}>
                            {r.name==='Bunker'?'per CFT':r.name==='Air Freight Charges'?'per 100 lbs':'per shipment'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--mid)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Local destination charges (TTD)</div>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Charge</th><th>Currency</th><th>Rate</th><th>Basis</th></tr></thead>
                    <tbody>
                      {local.length===0 ? <tr><td colSpan={4} style={{ color:'var(--light)', padding:'24px 16px' }}>No local rates configured</td></tr>
                      : local.map(r=>(
                        <tr key={r.id}>
                          <td style={{ fontWeight:500 }}>{r.name}</td>
                          <td><span className={`badge ${r.currency==='USD'?'badge-blue':'badge-green'}`}>{r.currency}</span></td>
                          <td><input className="input" type="number" step="0.0001" value={r.value} onChange={e=>setRateVal(r.id,e.target.value)} style={{ width:110, padding:'5px 8px' }} /></td>
                          <td style={{ fontSize:11, color:'var(--light)' }}>
                            {['Security Surcharge','Unstuffing','Local Inland Transport','DTHC/Other'].includes(r.name)?'per CFT':'per shipment'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <div style={{ marginTop:16, background:'#EEF3FB', borderRadius:10, padding:'11px 16px', fontSize:13, color:'#1A3F7A' }}>
            <strong>Note:</strong> Bunker, Security Surcharge, and Unstuffing are <strong>per CFT</strong> rates — multiplied by the shipment's cubic footage automatically.
          </div>
        </>
      )}

      {/* ── Brokerage tab ─────────────────────────────────────────────── */}
      {rateTab===1 && (
        <>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
            <button className="btn btn-green" onClick={saveBrokerage}>{saved?'✓ Saved':'Save brokerage rates'}</button>
          </div>
          <div className="grid-2">
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--mid)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Fixed brokerage fees (TTD)</div>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Service</th><th>Ocean</th><th>Air</th></tr></thead>
                  <tbody>
                    {brokerageRates.filter(r=>r.name!=='Value Based Brokerage Fee').map(r=>(
                      <tr key={r.id}>
                        <td style={{ fontWeight:500 }}>{r.name}</td>
                        <td><input className="input" type="number" step="0.01" value={r.rate_ocean} onChange={e=>setBRVal(r.id,'rate_ocean',e.target.value)} style={{ width:100, padding:'5px 8px' }} /></td>
                        <td><input className="input" type="number" step="0.01" value={r.rate_air} onChange={e=>setBRVal(r.id,'rate_air',e.target.value)} style={{ width:100, padding:'5px 8px' }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--mid)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Value-based brokerage — CIF tiers</div>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>CIF range (TTD)</th><th>Rate (%)</th><th>Minimum (TTD)</th></tr></thead>
                  <tbody>
                    {cifTiers.map(t=>(
                      <tr key={t.id}>
                        <td style={{ fontSize:12, color:'var(--mid)' }}>
                          TT${Number(t.cif_min).toLocaleString()} – {t.cif_max?'TT$'+Number(t.cif_max).toLocaleString():'above'}
                        </td>
                        <td>
                          <input className="input" type="number" step="0.001" value={t.rate} onChange={e=>setTierVal(t.id,'rate',e.target.value)} style={{ width:90, padding:'5px 8px' }} />
                        </td>
                        <td>
                          <input className="input" type="number" step="1" value={t.minimum_fee||0} onChange={e=>setTierVal(t.id,'minimum_fee',e.target.value)} style={{ width:100, padding:'5px 8px' }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop:12, background:'var(--cream)', borderRadius:8, padding:'10px 14px', fontSize:12, color:'var(--mid)' }}>
                Value-based fee is calculated on the CIF value (commercial value + freight charges × ROE). The highest applicable tier rate is applied with its minimum fee.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Transport tab ─────────────────────────────────────────────── */}
      {rateTab===2 && (
        <>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
            <button className="btn btn-green" onClick={saveTransport}>{saved?'✓ Saved':'Save transport rates'}</button>
          </div>
          <div className="grid-2">
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--mid)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Zone rates (TTD)</div>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Zone</th><th>Distance</th><th>Areas</th><th>Base rate</th><th>Per km</th></tr></thead>
                  <tbody>
                    {transportZones.map(z=>(
                      <tr key={z.id}>
                        <td style={{ fontWeight:600 }}>{z.zone}</td>
                        <td style={{ fontSize:11, color:'var(--light)', whiteSpace:'nowrap' }}>{z.distance_min}–{z.distance_max} km</td>
                        <td style={{ fontSize:11, color:'var(--mid)', maxWidth:160 }}>{z.example_areas}</td>
                        <td><input className="input" type="number" step="0.01" value={z.base_rate_ttd} onChange={e=>setZoneVal(z.id,'base_rate_ttd',e.target.value)} style={{ width:90, padding:'5px 8px' }} /></td>
                        <td><input className="input" type="number" step="0.01" value={z.per_km_ttd} onChange={e=>setZoneVal(z.id,'per_km_ttd',e.target.value)} style={{ width:80, padding:'5px 8px' }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--mid)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Add-on parameters (TTD)</div>
              <div className="card">
                {transportParams.map(p=>(
                  <div key={p.key} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                    <div>
                      <div style={{ fontWeight:500, fontSize:14 }}>{p.key.replace(/_/g,' ').replace(/ttd/gi,'(TTD)').replace(/minutes/gi,'(min)').replace(/block/gi,'block')}</div>
                      <div style={{ fontSize:11, color:'var(--light)' }}>{p.notes}</div>
                    </div>
                    <input className="input" type="number" step="0.01" value={p.value} onChange={e=>setParamVal(p.key,e.target.value)} style={{ width:100, padding:'6px 10px' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add rate modal */}
      {addModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setAddModal(false)}>
          <div className="modal">
            <div className="modal-title">Add new rate</div>
            <div style={{ background:'var(--cream)', borderRadius:8, padding:'10px 14px', fontSize:12, color:'var(--mid)', marginBottom:16 }}>
              Adding to: <strong>{origin}</strong> — <strong>{service==='ocean'?'Ocean LCL':'Air Freight'}</strong>
            </div>
            <div className="field"><label className="label">Charge name</label><input className="input" value={newRate.name} onChange={e=>setNewRate(r=>({...r,name:e.target.value}))} placeholder="e.g. Documentation Fee" autoFocus /></div>
            <div className="field-row">
              <div className="field"><label className="label">Category</label>
                <select className="select" value={newRate.category} onChange={e=>setNewRate(r=>({...r,category:e.target.value}))}>
                  <option value="freight">Freight (USD)</option><option value="local">Local (TTD)</option>
                </select>
              </div>
              <div className="field"><label className="label">Currency</label>
                <select className="select" value={newRate.currency} onChange={e=>setNewRate(r=>({...r,currency:e.target.value}))}>
                  <option value="USD">USD</option><option value="TTD">TTD</option>
                </select>
              </div>
            </div>
            <div className="field"><label className="label">Rate value</label><input className="input" type="number" step="0.01" value={newRate.value} onChange={e=>setNewRate(r=>({...r,value:e.target.value}))} placeholder="0.00" /></div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={()=>setAddModal(false)}>Cancel</button>
              <button className="btn btn-green" onClick={addRate}>Add rate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
